// There's a bug in TipTap/react that causes flushSync errors.
// Copying this solution from this PR: https://github.com/ueberdosis/tiptap/pull/4000/files#diff-13acb5e551812e195c1140c10ba5ac298a0005996d07756cfd77c2d4194cb350

import React, { useRef, useEffect, useState, DependencyList } from "react"

import { EditorOptions } from "@tiptap/core"
import { EditorContentProps, PureEditorContent, Editor } from "@tiptap/react"

function useForceUpdate() {
  const [, setValue] = useState(0)
  return () => setValue((value) => value + 1)
}

export const useEditor = (
  options: Partial<EditorOptions> = {},
  deps: DependencyList = []
) => {
  const [editor, setEditor] = useState<Editor | null>(null)
  const forceUpdate = useForceUpdate()
  const {
    onBeforeCreate,
    onBlur,
    onCreate,
    onDestroy,
    onFocus,
    onSelectionUpdate,
    onTransaction,
    onUpdate,
  } = options
  const onBeforeCreateRef = useRef(onBeforeCreate)
  const onBlurRef = useRef(onBlur)
  const onCreateRef = useRef(onCreate)
  const onDestroyRef = useRef(onDestroy)
  const onFocusRef = useRef(onFocus)
  const onSelectionUpdateRef = useRef(onSelectionUpdate)
  const onTransactionRef = useRef(onTransaction)
  const onUpdateRef = useRef(onUpdate)
  // This effect will handle updating the editor instance
  // when the event handlers change.
  useEffect(() => {
    if (!editor) {
      return
    }
    if (onBeforeCreate) {
      editor.off("beforeCreate", onBeforeCreateRef.current)
      editor.on("beforeCreate", onBeforeCreate)
      onBeforeCreateRef.current = onBeforeCreate
    }
    if (onBlur) {
      editor.off("blur", onBlurRef.current)
      editor.on("blur", onBlur)
      onBlurRef.current = onBlur
    }
    if (onCreate) {
      editor.off("create", onCreateRef.current)
      editor.on("create", onCreate)
      onCreateRef.current = onCreate
    }
    if (onDestroy) {
      editor.off("destroy", onDestroyRef.current)
      editor.on("destroy", onDestroy)
      onDestroyRef.current = onDestroy
    }
    if (onFocus) {
      editor.off("focus", onFocusRef.current)
      editor.on("focus", onFocus)
      onFocusRef.current = onFocus
    }
    if (onSelectionUpdate) {
      editor.off("selectionUpdate", onSelectionUpdateRef.current)
      editor.on("selectionUpdate", onSelectionUpdate)
      onSelectionUpdateRef.current = onSelectionUpdate
    }
    if (onTransaction) {
      editor.off("transaction", onTransactionRef.current)
      editor.on("transaction", onTransaction)
      onTransactionRef.current = onTransaction
    }
    if (onUpdate) {
      editor.off("update", onUpdateRef.current)
      editor.on("update", onUpdate)
      onUpdateRef.current = onUpdate
    }
  }, [
    onBeforeCreate,
    onBlur,
    onCreate,
    onDestroy,
    onFocus,
    onSelectionUpdate,
    onTransaction,
    onUpdate,
    editor,
  ])
  useEffect(() => {
    let isMounted = true
    const instance = new Editor(options)
    setEditor(instance)
    instance.on("transaction", () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (isMounted) {
            forceUpdate()
          }
        })
      })
    })

    return () => {
      isMounted = false
    }
  }, deps)

  useEffect(() => {
    return () => {
      editor?.destroy()
    }
  }, [editor])

  return editor
}

// EditorContent should be re-created whenever the Editor instance changes
const EditorContentWithKey = (props: EditorContentProps) => {
  const key = React.useMemo(() => {
    return Math.floor(Math.random() * 0xffffffff).toString()
  }, [props.editor])

  // Can't use JSX here because it conflicts with the type definition of Vue's JSX, so use createElement
  return React.createElement(PureEditorContent, {
    key,
    className: "grow flex flex-col",
    ...props,
  })
}

export const EditorContent = React.memo(EditorContentWithKey)
